import { Badge, Box } from '@palmetto/palmetto-components';
import { useCallback, type KeyboardEvent } from 'react';

const tabStyle: React.CSSProperties = {
  textDecoration: 'none',
  fontWeight: 500,
  cursor: 'pointer',
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const tabInnerStyle: React.CSSProperties = { transitionDuration: '0s' };

const tabHover = { color: 'primary' } as const;

export interface MilestoneTabProps {
  /** Position of this tab within the tablist (0 = the "All" tab). */
  index: number;
  /** Milestone type this tab selects. `undefined` represents the "All" tab. */
  type?: string;
  label: string;
  count: number;
  isActive: boolean;
  /** Stable selection callback. Passing `undefined` clears the milestone filter. */
  onSelect: (type?: string) => void;
  /** Stable navigation callback for arrow/Home/End roving focus. */
  onNavigate: (event: KeyboardEvent<HTMLDivElement>, index: number) => void;
  /** Stable ref registration so the parent can manage roving focus + scroll-into-view. */
  registerRef: (index: number, element: HTMLElement | null) => void;
}

export function MilestoneTab({
  index,
  type,
  label,
  count,
  isActive,
  onSelect,
  onNavigate,
  registerRef,
}: MilestoneTabProps) {
  const handleClick = useCallback(() => onSelect(type), [onSelect, type]);

  // Manual activation: Enter/Space select (and refetch); arrows/Home/End only move focus.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(type);
      } else {
        onNavigate(event, index);
      }
    },
    [onSelect, type, onNavigate, index],
  );

  const setRef = useCallback((element: HTMLDivElement | null) => registerRef(index, element), [registerRef, index]);

  return (
    <div
      ref={setRef}
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      className="milestone-tab"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={tabStyle}
    >
      <Box
        alignItems="center"
        padding="md"
        childGap="sm"
        className={isActive ? 'tab-active' : ''}
        color={isActive ? 'primary' : 'body-primary'}
        direction="row"
        hover={tabHover}
        style={tabInnerStyle}
      >
        <Box as="span" fontWeight="bold">
          {label}
        </Box>{' '}
        <Badge message={count} size="sm" variant={isActive ? 'primary' : 'default'} />
      </Box>
    </div>
  );
}
